package app.footyguide.core.locale

// ── Mercado de transmissão ────────────────────────────────────

/**
 * Mercado de transmissão do usuário.
 *
 * Determina o código usado em `channelsByCountry` no Firestore.
 * Um brasileiro morando nos EUA tem locale = "US" e language = "PT".
 *
 * MVP: apenas "BR" com dados reais. Outros aceitos mas sem canais.
 */
object BroadcastMarket {
    const val BR = "BR"
    const val US = "US"
    const val AR = "AR"
    const val GB = "GB"
    const val ES = "ES"
    const val PT = "PT"
    const val MX = "MX"
    const val FR = "FR"
    const val DE = "DE"
    const val IT = "IT"
    /** Canal sem restrição regional (ex: YouTube aberto). */
    const val WORLD = "WORLD"

    /** Mercados com dados reais no Firestore (MVP: só BR). */
    val available: List<String> = listOf(BR)

    val default: String = BR
}

// ── Idioma da interface ───────────────────────────────────────

/**
 * Idioma da UI.
 *
 * Independente do locale de transmissão — um brasileiro em Lisboa
 * pode ter locale = "PT" (canais portugueses) e language = "PT".
 * Um brasileiro em NYC: locale = "US", language = "PT".
 */
enum class UiLanguage(
    /** Código de 2 letras — chave em nameByLocale no Firestore. */
    val code: String,
    /** Nome exibido ao usuário nas configurações. */
    val displayName: String,
) {
    PT(code = "PT", displayName = "Português"),
    EN(code = "EN", displayName = "English"),
    ES(code = "ES", displayName = "Español"),
    FR(code = "FR", displayName = "Français"),
    DE(code = "DE", displayName = "Deutsch"),
    IT(code = "IT", displayName = "Italiano");

    companion object {
        /** Padrão: Português. Idioma ausente ou não suportado (≠ PT/ES/EN) cai em PT. */
        val default: UiLanguage = PT

        /**
         * Detecta o idioma a partir da preferência do sistema (ex: "pt-BR", "es-419", "en-US").
         * Suporta PT, ES e EN; qualquer outro fallback para PT (default).
         */
        fun fromSystemLanguage(tag: String?): UiLanguage {
            if (tag.isNullOrBlank()) return default
            val clean = tag.trim().lowercase()
            val primarySubtag = clean.split("-", "_").firstOrNull() ?: ""
            return when (primarySubtag) {
                "pt" -> PT
                "es" -> ES
                "en" -> EN
                else -> default
            }
        }

        fun fromCode(code: String): UiLanguage =
            entries.firstOrNull { it.code.equals(code, ignoreCase = true) } ?: default
    }
}
